/**
 * The orb's voice: a procedural "speaking" envelope, a short history of it (so ripples can
 * travel outward with the sound that made them), and an opt-in microphone analyser.
 *
 * Everything here is allocation-free once constructed, so it can run inside `useFrame`.
 *
 * @module
 */
import { mulberry32 } from '@/lib/math'

// ---------------------------------------------------------------------------------------
// Organic 1D noise
// ---------------------------------------------------------------------------------------

function lattice(i: number, seed: number): number {
  let h = Math.imul(i ^ seed, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return ((h >>> 0) / 4294967295) * 2 - 1
}

/**
 * Smooth 1D gradient noise in roughly −1..1 (quintic fade, C2-continuous). Drives the
 * drift, rotation and breathing irregularity so nothing loops mechanically.
 */
export function noise1(x: number, seed = 0x9e3779b9): number {
  const i = Math.floor(x)
  const f = x - i
  const u = f * f * f * (f * (f * 6 - 15) + 10)
  const a = lattice(i, seed) * f
  const b = lattice(i + 1, seed) * (f - 1)
  return (a + (b - a) * u) * 2
}

// ---------------------------------------------------------------------------------------
// Procedural speech
// ---------------------------------------------------------------------------------------

interface Syllable {
  start: number
  end: number
  amplitude: number
}

/** Seconds a syllable keeps ringing after it ends. */
const TAIL = 0.7
const ATTACK = 0.22

/**
 * Syllable-like bursts grouped into soft phrases, with calm pauses between them, as if
 * Vokarta were talking quietly. Deterministic for a seed; `level(t)` is a pure function of
 * time once the schedule has been generated (it is extended lazily as time advances).
 */
export class ProceduralVoice {
  private readonly random: () => number
  private readonly syllables: Syllable[] = []
  private horizon: number

  constructor(seed = 0x5eed, firstPhraseAt = 0.35) {
    this.random = mulberry32(seed)
    this.horizon = firstPhraseAt
  }

  /** Grows the schedule until it covers `t`. Old syllables are dropped. */
  private extend(t: number): void {
    while (this.horizon < t + 2) {
      const count = 2 + Math.floor(this.random() * 5)
      const gain = 0.62 + this.random() * 0.38
      let cursor = this.horizon
      for (let i = 0; i < count; i++) {
        const duration = 0.11 + this.random() * 0.19
        const stress = i === 0 ? 1 : 0.55 + this.random() * 0.45
        this.syllables.push({ start: cursor, end: cursor + duration, amplitude: stress * gain })
        // Short gaps between syllables, now and then a longer breath mid-phrase.
        cursor += duration + 0.05 + this.random() * (this.random() < 0.18 ? 0.34 : 0.11)
      }
      this.horizon = cursor + 2.3 + this.random() * 3.6
    }
    // Keep only what the history window can still ask for.
    while (this.syllables.length > 0 && this.syllables[0].end + TAIL < t - 4) {
      this.syllables.shift()
    }
  }

  /** The speaking envelope at time `t` (seconds), in 0..1. */
  level(t: number): number {
    if (t < 0) return 0
    this.extend(t)
    let sum = 0
    for (let i = 0; i < this.syllables.length; i++) {
      const s = this.syllables[i]
      if (s.start > t) break
      if (s.end + TAIL < t) continue
      const duration = s.end - s.start
      const x = (t - s.start) / duration
      let shape: number
      if (x < ATTACK) {
        const a = x / ATTACK
        shape = a * a * (3 - 2 * a)
      } else if (x < 1) {
        shape = 1 - 0.25 * ((x - ATTACK) / (1 - ATTACK))
      } else {
        shape = 0.75 * Math.exp(-(t - s.end) * 9)
      }
      sum += shape * s.amplitude
    }
    return Math.min(1, sum)
  }
}

// ---------------------------------------------------------------------------------------
// Envelope history
// ---------------------------------------------------------------------------------------

/**
 * A ring buffer of (time, value) samples, resampled at arbitrary past times. The orb's
 * ripples read `sample(now - delay)` so a burst travels outward instead of appearing
 * everywhere at once.
 */
export class EnvelopeHistory {
  private readonly times: Float64Array
  private readonly values: Float32Array
  private head = -1
  private size = 0

  constructor(capacity = 256) {
    this.times = new Float64Array(capacity)
    this.values = new Float32Array(capacity)
  }

  push(time: number, value: number): void {
    const capacity = this.times.length
    this.head = (this.head + 1) % capacity
    this.times[this.head] = time
    this.values[this.head] = value
    this.size = Math.min(this.size + 1, capacity)
  }

  /** Linear interpolation at `time`; 0 before the first sample. */
  sample(time: number): number {
    if (this.size === 0) return 0
    const capacity = this.times.length
    let newer = this.head
    if (time >= this.times[newer]) return this.values[newer]
    for (let n = 1; n < this.size; n++) {
      const older = (this.head - n + capacity) % capacity
      const t0 = this.times[older]
      if (t0 <= time) {
        const t1 = this.times[newer]
        const f = t1 > t0 ? (time - t0) / (t1 - t0) : 0
        return this.values[older] + (this.values[newer] - this.values[older]) * f
      }
      newer = older
    }
    return 0
  }

  /** Fills `out[i]` with the value `i * step` seconds before `now`. */
  fill(out: number[], now: number, step: number): void {
    for (let i = 0; i < out.length; i++) out[i] = this.sample(now - i * step)
  }
}

// ---------------------------------------------------------------------------------------
// Microphone (opt-in, local only)
// ---------------------------------------------------------------------------------------

/** Where the hold-to-talk microphone is. */
export type MicStatus = 'idle' | 'requesting' | 'listening' | 'denied'

/** Smoothed levels, 0..1. */
export interface MicLevels {
  rms: number
  low: number
  mid: number
  high: number
}

/**
 * Hold-to-talk microphone. `start()` asks for the mic (getUserMedia) and feeds an
 * AnalyserNode; `stop()` stops every track at once. Audio is analysed in memory, frame by
 * frame, and never recorded, stored or sent anywhere.
 */
export class MicInput {
  status: MicStatus = 'idle'
  readonly levels: MicLevels = { rms: 0, low: 0, mid: 0, high: 0 }
  onStatus: ((status: MicStatus) => void) | null = null

  private wanted = false
  private context: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private sink: GainNode | null = null
  private stream: MediaStream | null = null
  private frequency: Uint8Array<ArrayBuffer> = new Uint8Array(0)
  private waveform: Float32Array<ArrayBuffer> = new Float32Array(0)

  private setStatus(status: MicStatus): void {
    if (this.status === status) return
    this.status = status
    this.onStatus?.(status)
  }

  /** Starts listening (call from a user gesture). Resolves once granted, denied or released. */
  async start(): Promise<void> {
    this.wanted = true
    if (this.status === 'listening' || this.status === 'requesting') return
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
    if (!media?.getUserMedia || typeof AudioContext === 'undefined') {
      this.setStatus('denied')
      return
    }
    this.setStatus('requesting')
    try {
      // Created and resumed inside the gesture, so autoplay policies allow it.
      this.context ??= new AudioContext()
      void this.context.resume()
      const stream = await media.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
      if (!this.wanted) {
        stream.getTracks().forEach((track) => track.stop())
        this.setStatus('idle')
        return
      }
      this.stream = stream
      const context = this.context
      this.analyser = context.createAnalyser()
      this.analyser.fftSize = 1024
      this.analyser.smoothingTimeConstant = 0.55
      this.frequency = new Uint8Array(this.analyser.frequencyBinCount)
      this.waveform = new Float32Array(this.analyser.fftSize)
      this.source = context.createMediaStreamSource(stream)
      // A silent sink keeps the graph pulling in every browser; nothing is audible.
      this.sink = context.createGain()
      this.sink.gain.value = 0
      this.source.connect(this.analyser)
      this.analyser.connect(this.sink)
      this.sink.connect(context.destination)
      this.setStatus('listening')
    } catch {
      this.release()
      this.setStatus('denied')
    }
  }

  /** Stops listening: every track ends immediately and the audio graph is torn down. */
  stop(): void {
    this.wanted = false
    if (this.status === 'denied') return
    this.release()
    this.setStatus('idle')
  }

  private release(): void {
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
    this.source?.disconnect()
    this.analyser?.disconnect()
    this.sink?.disconnect()
    this.source = null
    this.analyser = null
    this.sink = null
    void this.context?.suspend().catch(() => undefined)
  }

  /** Reads the analyser and updates `levels` (smoothed by `dt`). Call once per frame. */
  sample(dt: number): void {
    const levels = this.levels
    const analyser = this.analyser
    const k = 1 - Math.exp(-14 * dt)
    if (!analyser || this.status !== 'listening') {
      levels.rms += (0 - levels.rms) * k
      levels.low += (0 - levels.low) * k
      levels.mid += (0 - levels.mid) * k
      levels.high += (0 - levels.high) * k
      return
    }
    analyser.getFloatTimeDomainData(this.waveform)
    let sum = 0
    for (let i = 0; i < this.waveform.length; i++) sum += this.waveform[i] * this.waveform[i]
    const rms = Math.sqrt(sum / this.waveform.length)

    analyser.getByteFrequencyData(this.frequency)
    const binHz = (this.context?.sampleRate ?? 48000) / analyser.fftSize
    const band = (from: number, to: number) => {
      const a = Math.max(1, Math.floor(from / binHz))
      const b = Math.min(this.frequency.length - 1, Math.ceil(to / binHz))
      let total = 0
      for (let i = a; i <= b; i++) total += this.frequency[i]
      return total / ((b - a + 1) * 255)
    }
    // Speech sits well below full scale: lift it with soft curves into a useful 0..1.
    const lift = (v: number, gain: number) => 1 - Math.exp(-v * gain)
    levels.rms += (lift(rms, 9) - levels.rms) * k
    levels.low += (lift(band(80, 300), 2.6) - levels.low) * k
    levels.mid += (lift(band(300, 2000), 3.2) - levels.mid) * k
    levels.high += (lift(band(2000, 6000), 5) - levels.high) * k
  }

  /** Releases everything, including the AudioContext. Call on unmount. */
  dispose(): void {
    this.wanted = false
    this.release()
    void this.context?.close().catch(() => undefined)
    this.context = null
    this.onStatus = null
    this.status = 'idle'
  }
}
